import type { CloudTemplate } from "./types";

type DebianCandidate = {
  template: CloudTemplate;
  version: number[];
  qcow2: boolean;
  preinstalled: boolean;
};

function debianVersion(templateId: string): number[] | null {
  const match = templateId.match(/\bdebian[-_\s]*(\d+(?:\.\d+)*)/i);
  if (!match) return null;
  return match[1]!.split(".").map(Number);
}

function compareVersionDescending(left: number[], right: number[]): number {
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (right[index] ?? 0) - (left[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

export function selectDefaultTemplateId(templates: CloudTemplate[], qcow2Enabled = true): string | undefined {
  const candidates = templates.flatMap<DebianCandidate>((template) => {
    if (!qcow2Enabled && template.type.trim().toLocaleUpperCase("en-US") !== "ISO") return [];
    const version = debianVersion(template.id);
    if (!version) return [];
    const searchableText = `${template.id} ${template.description}`.toLocaleLowerCase("en-US");
    return [{
      template,
      version,
      qcow2: template.type.toLocaleUpperCase("en-US") === "QCOW2",
      preinstalled: searchableText.includes("preinstalled"),
    }];
  });
  if (candidates.length === 0) return undefined;

  const availableMajors = candidates.map((candidate) => candidate.version[0] ?? Number.NEGATIVE_INFINITY);
  const preferredDebianVersion = !qcow2Enabled && availableMajors.includes(13)
    ? 13
    : Math.max(...availableMajors);
  return candidates
    .filter((candidate) => (candidate.version[0] ?? Number.NEGATIVE_INFINITY) === preferredDebianVersion)
    .sort((left, right) =>
      Number(right.qcow2) - Number(left.qcow2)
      || Number(right.preinstalled) - Number(left.preinstalled)
      || compareVersionDescending(left.version, right.version)
      || left.template.id.localeCompare(right.template.id, "en"),
    )[0]?.template.id;
}
